import { useState } from "react";
import axios from "axios";

const api = axios.create({ baseURL: "http://localhost:8000" });

interface Expert {
  name: string;
  title: string;
  company: string;
  linkedin_url: string;
  github_url: string;
  scholar_url: string;
  source: string;
  domain_relevance: number;
  schema_overlap: number;
  seniority_score: number;
  recency_score: number;
  availability_signal: number;
  overall_score: number;
  outreach_message: string;
  reasoning: string;
}

const SOURCE_COLORS: Record<string, { color: string; bg: string }> = {
  linkedin: { color: "#0a66c2", bg: "rgba(10,102,194,0.08)" },
  github: { color: "#24292f", bg: "rgba(36,41,47,0.08)" },
  scholar: { color: "#4285f4", bg: "rgba(66,133,244,0.08)" },
  internal: { color: "#059669", bg: "rgba(5,150,105,0.08)" },
};

export default function ExpertFinder() {
  const [description, setDescription] = useState("Driver churn prediction for a rideshare startup");
  const [columns, setColumns] = useState("Surge_Session_Pct, First_Week_Rides, Rider_Cancel_Rate, Avg_Consecutive_Hours, Airport_Ride_Pct, Current_Rating");
  const [industry, setIndustry] = useState("rideshare");
  const [experts, setExperts] = useState<Expert[]>([]);
  const [loading, setLoading] = useState(false);
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null);

  const handleSearch = async () => {
    setLoading(true);
    setExperts([]);
    try {
      const res = await api.post("/api/experts/search", {
        model_description: description,
        target_variable: "",
        columns: columns.split(",").map(c => c.trim()).filter(Boolean),
        industry,
      });
      setExperts(res.data.experts);
    } catch {
      // fallback
    }
    setLoading(false);
  };

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", overflow: "hidden", background: "#fff" }}>
      {/* Header */}
      <div style={{ padding: "20px 32px 14px", borderBottom: "1px solid rgba(0,0,0,0.06)", flexShrink: 0 }}>
        <h2 style={{ fontSize: 17, fontWeight: 700, margin: 0 }}>Find Experts</h2>
        <p style={{ fontSize: 12, color: "rgba(0,0,0,0.5)", margin: "3px 0 0" }}>
          Describe your model problem. We search LinkedIn, GitHub, Scholar, and our network.
        </p>
      </div>

      <div style={{ flex: 1, overflow: "auto", padding: "20px 32px 40px" }}>
        {/* Search form */}
        <div style={{ maxWidth: 700, margin: "0 auto" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 14, marginBottom: 24 }}>
            <div>
              <div style={{ fontSize: 10, fontWeight: 600, color: "rgba(0,0,0,0.4)", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.04em" }}>What are you building?</div>
              <input
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="e.g. Driver churn prediction for a rideshare startup"
                style={{
                  width: "100%", padding: "10px 14px", borderRadius: 10, border: "1px solid rgba(0,0,0,0.1)",
                  fontSize: 13, outline: "none",
                }}
              />
            </div>
            <div>
              <div style={{ fontSize: 10, fontWeight: 600, color: "rgba(0,0,0,0.4)", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.04em" }}>Dataset columns (comma-separated)</div>
              <textarea
                value={columns}
                onChange={e => setColumns(e.target.value)}
                placeholder="e.g. Surge_Session_Pct, First_Week_Rides, Rider_Cancel_Rate"
                rows={2}
                style={{
                  width: "100%", padding: "10px 14px", borderRadius: 10, border: "1px solid rgba(0,0,0,0.1)",
                  fontSize: 12, fontFamily: "'IBM Plex Mono', monospace", outline: "none", resize: "vertical",
                }}
              />
            </div>
            <div style={{ display: "flex", gap: 12, alignItems: "flex-end" }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 10, fontWeight: 600, color: "rgba(0,0,0,0.4)", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.04em" }}>Industry</div>
                <div style={{ display: "flex", gap: 6 }}>
                  {["rideshare", "agriculture", "fintech", "healthcare", "retail", "other"].map(ind => (
                    <button key={ind} onClick={() => setIndustry(ind)} style={{
                      padding: "6px 14px", borderRadius: 8, cursor: "pointer",
                      border: industry === ind ? "1.5px solid #1e3a6e" : "1px solid rgba(0,0,0,0.08)",
                      background: industry === ind ? "rgba(30,58,110,0.04)" : "#fff",
                      fontSize: 11, fontWeight: industry === ind ? 700 : 500,
                      color: industry === ind ? "#1e3a6e" : "rgba(0,0,0,0.5)",
                    }}>{ind}</button>
                  ))}
                </div>
              </div>
              <button
                onClick={handleSearch}
                disabled={loading || !description.trim()}
                style={{
                  padding: "10px 28px", borderRadius: 10, border: "none",
                  background: loading ? "rgba(0,0,0,0.1)" : "linear-gradient(135deg, #1e3a6e, #3b7dd8)",
                  color: loading ? "rgba(0,0,0,0.4)" : "#fff",
                  fontSize: 13, fontWeight: 600, cursor: loading ? "default" : "pointer",
                  boxShadow: loading ? "none" : "0 4px 16px rgba(30,58,110,0.2)",
                  whiteSpace: "nowrap",
                }}
              >
                {loading ? "Searching..." : "Find Experts"}
              </button>
            </div>
          </div>

          {/* Results */}
          {experts.length > 0 && (
            <>
              <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "rgba(0,0,0,0.35)", marginBottom: 10 }}>
                {experts.length} experts found — ranked by relevance
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {experts.map((expert, idx) => {
                  const sc = SOURCE_COLORS[expert.source] || SOURCE_COLORS.linkedin;
                  const isExpanded = expandedIdx === idx;
                  const scoreColor = expert.overall_score > 0.8 ? "#10b981" : expert.overall_score > 0.65 ? "#f59e0b" : "#ef4444";

                  return (
                    <div key={idx} style={{
                      borderRadius: 12, border: `1px solid ${isExpanded ? "rgba(0,0,0,0.12)" : "rgba(0,0,0,0.06)"}`,
                      background: "#fff", overflow: "hidden",
                      boxShadow: isExpanded ? "0 2px 12px rgba(0,0,0,0.06)" : "none",
                      transition: "all 0.2s ease",
                    }}>
                      {/* Header row */}
                      <div
                        onClick={() => setExpandedIdx(isExpanded ? null : idx)}
                        style={{ padding: "14px 18px", cursor: "pointer", display: "flex", alignItems: "center", gap: 14 }}
                      >
                        {/* Avatar */}
                        <div style={{
                          width: 40, height: 40, borderRadius: "50%", flexShrink: 0,
                          background: `linear-gradient(135deg, ${sc.bg}, rgba(99,102,241,0.08))`,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          fontSize: 13, fontWeight: 700, color: sc.color,
                        }}>
                          {expert.name.split(" ").map(n => n[0]).join("").slice(0, 2)}
                        </div>

                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 2 }}>
                            <span style={{ fontSize: 14, fontWeight: 700 }}>{expert.name}</span>
                            <span style={{ fontSize: 8, fontWeight: 700, padding: "2px 6px", borderRadius: 4, background: sc.bg, color: sc.color, textTransform: "uppercase" }}>{expert.source}</span>
                          </div>
                          <div style={{ fontSize: 12, color: "rgba(0,0,0,0.5)" }}>{expert.title} @ {expert.company}</div>
                        </div>

                        {/* Score */}
                        <div style={{ textAlign: "right", flexShrink: 0 }}>
                          <div style={{ fontSize: 20, fontWeight: 700, fontFamily: "'IBM Plex Mono', monospace", color: scoreColor }}>
                            {(expert.overall_score * 100).toFixed(0)}
                          </div>
                          <div style={{ fontSize: 9, color: "rgba(0,0,0,0.35)" }}>match score</div>
                        </div>

                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="2" strokeLinecap="round"
                          style={{ opacity: 0.2, transition: "transform 0.2s", transform: isExpanded ? "rotate(180deg)" : "rotate(0)" }}>
                          <polyline points="6 9 12 15 18 9" />
                        </svg>
                      </div>

                      {/* Expanded details */}
                      {isExpanded && (
                        <div style={{ padding: "0 18px 18px", borderTop: "1px solid rgba(0,0,0,0.04)" }}>
                          {/* Score breakdown */}
                          <div style={{ display: "flex", gap: 8, marginTop: 14, marginBottom: 14, flexWrap: "wrap" }}>
                            {[
                              { label: "Domain", value: expert.domain_relevance },
                              { label: "Schema", value: expert.schema_overlap },
                              { label: "Seniority", value: expert.seniority_score },
                              { label: "Recency", value: expert.recency_score },
                              { label: "Availability", value: expert.availability_signal },
                            ].map(s => (
                              <div key={s.label} style={{ flex: 1, minWidth: 80, padding: "8px 10px", borderRadius: 8, background: "rgba(0,0,0,0.02)", textAlign: "center" }}>
                                <div style={{ fontSize: 16, fontWeight: 600, fontFamily: "'IBM Plex Mono', monospace", color: s.value > 0.8 ? "#10b981" : s.value > 0.6 ? "#f59e0b" : "#ef4444" }}>
                                  {(s.value * 100).toFixed(0)}%
                                </div>
                                <div style={{ fontSize: 9, color: "rgba(0,0,0,0.4)" }}>{s.label}</div>
                              </div>
                            ))}
                          </div>

                          {/* Links */}
                          <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
                            {expert.linkedin_url && (
                              <a href={expert.linkedin_url} target="_blank" rel="noreferrer" style={{
                                padding: "6px 12px", borderRadius: 6, background: "rgba(10,102,194,0.06)",
                                fontSize: 11, color: "#0a66c2", textDecoration: "none", fontWeight: 600,
                              }}>LinkedIn</a>
                            )}
                            {expert.github_url && (
                              <a href={expert.github_url} target="_blank" rel="noreferrer" style={{
                                padding: "6px 12px", borderRadius: 6, background: "rgba(36,41,47,0.06)",
                                fontSize: 11, color: "#24292f", textDecoration: "none", fontWeight: 600,
                              }}>GitHub</a>
                            )}
                            {expert.scholar_url && (
                              <a href={expert.scholar_url} target="_blank" rel="noreferrer" style={{
                                padding: "6px 12px", borderRadius: 6, background: "rgba(66,133,244,0.06)",
                                fontSize: 11, color: "#4285f4", textDecoration: "none", fontWeight: 600,
                              }}>Scholar</a>
                            )}
                          </div>

                          {/* Outreach message */}
                          <div style={{
                            padding: "14px 16px", borderRadius: 10,
                            background: "rgba(30,58,110,0.03)", border: "1px solid rgba(30,58,110,0.08)",
                          }}>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                              <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "rgba(0,0,0,0.35)" }}>Generated outreach</div>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigator.clipboard.writeText(expert.outreach_message);
                                }}
                                style={{
                                  padding: "3px 10px", borderRadius: 6, border: "1px solid rgba(30,58,110,0.15)",
                                  background: "#fff", fontSize: 10, fontWeight: 600, color: "#1e3a6e", cursor: "pointer",
                                }}
                              >Copy</button>
                            </div>
                            <div style={{ fontSize: 12, color: "rgba(0,0,0,0.6)", lineHeight: 1.7, whiteSpace: "pre-wrap" }}>
                              {expert.outreach_message}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
